import { useCallback, useEffect, useState } from 'react';

/**
 * The version of react slick that we are using seems to have issues as mentioned in the following issue:
 * https://github.com/akiran/react-slick/discussions/2252
 *
 * This hook is a workaround to allow for responsive slides to show based on the window width.
 *
 * @param defaultSlidesToShow
 * @param breakpoints - [breakpoint, slidesToShow] (smallest breakpoint to largest)
 * @returns
 */
const useResponsiveSlidesToShow = (
  defaultSlidesToShow: number,
  breakpoints: [number, number][]
) => {
  const [slidesToShow, setSlidesToShow] = useState(defaultSlidesToShow);

  const getSlidesToShow = useCallback(() => {
    const width = window.innerWidth;

    for (const [breakpoint, slidesToShow] of breakpoints) {
      if (width <= breakpoint) {
        return slidesToShow;
      }
    }

    return defaultSlidesToShow;
  }, [breakpoints, defaultSlidesToShow]);

  useEffect(() => {
    const handleResize = () => setSlidesToShow(getSlidesToShow());
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [getSlidesToShow]);

  return slidesToShow;
};

export { useResponsiveSlidesToShow };
